import {getAddressCountry, getAddressValidationError, getCountryCode, hasPoBox, normalizeAddressDetails, poBoxError} from './address-details';

export const registeredAgentName = (agent: any) =>
    [agent?.firstName, agent?.middleName, agent?.lastName, agent?.titleName].filter(Boolean).join(' ');

const addressFields = ['street1', 'street2', 'city', 'state', 'zipCode'];
const memberFields = ['firstName', 'lastName', 'middleName', 'titleName', 'title', 'type', 'address', 'city', 'state', 'zipCode'];

/** Stored fields can be null; the editors and the server expect text, so missing values become ''. */
const withText = (value: any, fields: string[]) => {
    const result = {...(value ?? {})};
    for (const field of fields) if (typeof result[field] !== 'string') result[field] = result[field] == null ? '' : String(result[field]);
    return result;
};

/**
 * The company as the visitor reviews it: missing parts become empty, editable defaults, countries become codes, and ZIP
 * and state formats get the same repairs the server makes, so the visitor is not asked to fix those.
 * keepRegisteredAgent leaves the stored agent untouched, for a designation the server requires to stay exactly as stored.
 */
export function prepareCompanyReview(company: any, {keepRegisteredAgent = false}: {keepRegisteredAgent?: boolean} = {}) {
    const address = (value: any) => {
        const normalized = normalizeAddressDetails(withText(value, addressFields));
        return {...normalized, country: getCountryCode(getAddressCountry(normalized))};
    };
    const officer = (value: any) => {
        const normalized = normalizeAddressDetails(withText(value, memberFields));
        return {...normalized, country: getCountryCode(getAddressCountry(normalized))};
    };
    const agent = () => {
        // The server reads a registered agent without a country as a US agent.
        const stored = {firstName: '', address: '', city: '', state: 'FL', zipCode: '', ...company.registeredAgent};
        const country = stored.country == null || !String(stored.country).trim() ? 'US' : getCountryCode(stored.country) ?? stored.country;
        return normalizeAddressDetails({...withText(stored, memberFields), country});
    };
    const copy = JSON.parse(JSON.stringify(company));
    return {
        ...copy,
        principalAddress: address(company.principalAddress),
        mailingAddress: address(company.mailingAddress ?? company.principalAddress),
        registeredAgent: keepRegisteredAgent ? copy.registeredAgent : agent(),
        officers: (company.officers ?? []).map(officer),
    };
}

/* Client-side mirrors of the server's review rules (CompanyReviewService / ApplicationInputValidator). */

const feinSpellings: Record<string, string> = {'': '', 'NONE': 'Not Applicable', 'N/A': 'Not Applicable', 'NA': 'Not Applicable',
    'NOT APPLICABLE': 'Not Applicable', 'APPLIED FOR': 'Applied For'};

export function feinError(fein?: string | null): string | null {
    const value = (fein ?? '').trim();
    return value.toUpperCase() in feinSpellings || /^\d{2}-?\d{7}$/.test(value) ? null : 'Enter a valid nine-digit FEIN in the format xx-xxxxxxx.';
}

export function principalAddressError(address: any): string | null {
    return getAddressValidationError(address ?? {}) ?? (hasPoBox(address?.street1, address?.street2) ? poBoxError : null);
}

export function mailingAddressError(address: any): string | null {
    return getAddressValidationError(address ?? {});
}

/** The agent's name as the server compares it with the company name. */
const agentFullName = (agent: any) => `${(agent?.firstName ?? '').trim()} ${(agent?.lastName ?? '').trim()}`.trim();

export function registeredAgentError(agent: any, companyName: string, {requireType = false}: {requireType?: boolean} = {}): string | null {
    if (!agent) return 'Add the registered agent.';
    if (requireType && !['P', 'C'].includes(agent.type)) return 'Select Individual or Business entity for the registered agent.';
    if (!(agent.firstName ?? '').trim()) return 'You need to enter a name for the registered agent.';
    const address = normalizeAddressDetails({street1: agent.address ?? '', city: agent.city, state: agent.state, zipCode: agent.zipCode,
        country: agent.country == null ? 'US' : agent.country});
    const error = getAddressValidationError(address);
    if (error) return error;
    if (hasPoBox(address.street1)) return poBoxError;
    if ((address.state ?? '').trim().toUpperCase() !== 'FL' || getCountryCode(getAddressCountry(address)) !== 'US') {
        return 'The registered agent must have a Florida street address.';
    }
    // As the server's CompanyReviewService::isOwnName: case, spacing and punctuation aside ("ACME, LLC" is "ACME LLC").
    const nameKey = (name: string) => name.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (nameKey(agentFullName(agent)) !== '' && nameKey(agentFullName(agent)) === nameKey(companyName ?? '')) return 'A company cannot be its own registered agent.';
    return null;
}

export function officerError(officer: any): string | null {
    if (!officer || officer.remove === true) return null;
    if (!(officer.title ?? '').trim()) return 'You need to enter a title for this officer.';
    if (!(officer.firstName ?? '').trim()) return 'You need to enter a name.';
    return getAddressValidationError(officer);
}

export const hasActiveOfficer = (officers: any[] | null | undefined) => (officers ?? []).some(officer => officer?.remove !== true);
